/**
 * Menu Search Bar Component - Tree Filter Style
 * 입력값을 사이드바로 올려보내면 Sidebar가 메뉴 트리를 그 자리에서 필터링한다.
 *
 * - 로컬 이름/코드 부분일치: 입력 즉시 반영 (사이드바에 실제로 그려진 메뉴)
 * - 매뉴얼 기반 시맨틱/키워드 검색: 2글자 이상일 때 menu_id 목록을 추가로 전달
 *   → 사이드바는 둘의 합집합으로 필터링하고, 매칭된 메뉴의 상위 메뉴를 모두 유지·펼침
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import { Search, X } from 'lucide-react';
import { menuSearchService } from '@/services/menuSearchService';
import type { MenuSearchResult } from '@/types/menuSearch';
import { useDebounce } from '@/hooks/useDebounce';
import { cn } from '@/lib/utils';

interface MenuSearchBarProps {
  /** 검색어와 (있으면) 시맨틱 검색 결과를 사이드바로 전달 */
  onSearchChange: (query: string, results: MenuSearchResult[]) => void;
}

export function MenuSearchBar({ onSearchChange }: MenuSearchBarProps) {
  const [query, setQuery] = useState('');
  // 결과는 어떤 검색어에 대한 것인지 함께 보관 (검색어 변경 중 이전 결과가 새는 것 방지)
  const [results, setResults] = useState<{ query: string; items: MenuSearchResult[] }>({
    query: '',
    items: [],
  });
  const [isLoading, setIsLoading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const debouncedQuery = useDebounce(query, 250);

  // 시맨틱/키워드 검색 (2글자 이상)
  useEffect(() => {
    const trimmed = debouncedQuery.trim();

    if (trimmed.length < 2) {
      setResults({ query: trimmed, items: [] });
      setIsLoading(false);
      return;
    }

    let cancelled = false;
    setIsLoading(true);

    menuSearchService
      .search(trimmed, { limit: 20 })
      .then((response) => {
        if (!cancelled) setResults({ query: trimmed, items: response.results });
      })
      .catch((error) => {
        console.error('메뉴 검색 오류:', error);
        if (!cancelled) setResults({ query: trimmed, items: [] });
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [debouncedQuery]);

  // 검색어/결과를 사이드바로 전달 (검색어가 바뀐 직후엔 로컬 필터만 적용)
  useEffect(() => {
    const trimmed = query.trim();
    onSearchChange(trimmed, results.query === trimmed ? results.items : []);
  }, [query, results, onSearchChange]);

  // 검색 초기화
  const handleClear = useCallback(() => {
    setQuery('');
    setResults({ query: '', items: [] });
    inputRef.current?.focus();
  }, []);

  // 전역 단축키 (Ctrl+M / Cmd+M) → 검색창 포커스
  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.key === 'm' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        inputRef.current?.focus();
      }
    };
    document.addEventListener('keydown', down);
    return () => document.removeEventListener('keydown', down);
  }, []);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      handleClear();
    }
  };

  return (
    <div className="relative">
      <Search
        className={cn(
          'absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4',
          isLoading ? 'text-blue-500 animate-pulse' : 'text-muted-foreground',
        )}
      />
      <input
        ref={inputRef}
        type="text"
        placeholder="메뉴 검색... (⌘M)"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        onKeyDown={handleKeyDown}
        className={cn(
          'w-full pl-9 pr-9 py-2 text-sm',
          'bg-background border rounded-md',
          'focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-0',
          'placeholder:text-muted-foreground',
          'transition-colors',
        )}
      />
      {query && (
        <button
          onClick={handleClear}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
          title="검색 초기화 (Esc)"
        >
          <X className="h-4 w-4" />
        </button>
      )}
    </div>
  );
}
